/**
 * Calculador de parentesco en espanol.
 *
 * Responde a "quien es B de A". El resultado se lee desde A: si devuelve
 * "tio abuelo", quiere decir que B es el tio abuelo de A.
 *
 * Como funciona, en tres pasos:
 *
 *  1. Hermandad directa, preguntando al indice. Hay que hacerlo aparte porque
 *     dos personas pueden ser hermanas SIN tener ningun ascendiente comun:
 *     el arbol real tiene una familia sin padre ni madre cuyo unico trabajo
 *     es declarar que dos personas son hermanas. Por ancestros comunes eso no
 *     se ve.
 *
 *  2. Sangre, por ascendiente comun mas cercano. De cada persona se saca el
 *     mapa de ascendientes con la distancia y la rama, se cruzan, y de la
 *     pareja de distancias (subidaA, subidaB) sale el termino.
 *
 *  3. Afinidad, si no hay sangre: se prueba a saltar por una pareja, en los
 *     dos sentidos, y se nombran los casos que el espanol tiene nombrados
 *     (suegro, cunado, yerno, nuera, concuno, padrastro, hijastro,
 *     hermanastro). Lo que no tiene nombre propio se describe en claro
 *     ("esposo de tu prima segunda") en vez de inventarse un termino.
 *
 * Lo que NO se simplifica, porque es justo lo que se pidio:
 *
 *  - Medios hermanos. La "mitad" se detecta contando cuantos ascendientes
 *    comunes hay al nivel mas cercano: dos (una pareja) es parentesco
 *    completo, uno solo es media sangre. Eso se propaga a los colaterales,
 *    asi que salen "medio tio" y "primo por una sola rama".
 *
 *  - Uniones multiples. Una persona puede tener varias parejas y ser
 *    progenitora en varias familias; los ascendientes se recorren por TODAS
 *    las familias en las que es hija, no solo por la primera.
 *
 *  - Via paterna o materna, que sale de por que rama se llega al ascendiente
 *    comun. Si se llega por las dos (ramas que se cruzan) se dice.
 */
import type { Ascendiente, Indice } from "./grafo.js";
import type { Sexo } from "./model.js";

export type ClaseParentesco = "misma" | "sanguineo" | "afin" | "ninguno";
export type Via = "paterna" | "materna" | "ambas";

export interface Parentesco {
  clase: ClaseParentesco;
  /** Termino completo: "prima hermana una vez removida", "tio abuelo". */
  termino: string;
  /**
   * Version breve, para la etiqueta que va bajo el nombre en el diagrama.
   *
   * El termino completo es correcto pero a veces larguisimo ("prima hermana
   * por una sola rama de tu companera"), y puesto bajo cada nombre convierte
   * el diagrama en un muro de texto. El breve usa lo que el espanol ya tiene
   * para esto: los parentescos por afinidad se nombran "politico/a" sobre el
   * de sangre equivalente, asi que esa misma persona es, en corto, "prima
   * politica".
   */
  breve: string;
  /** Frase completa, pensada para leerse tal cual. */
  explicacion: string;
  via?: Via;
  /** Generaciones hacia arriba desde A hasta el ascendiente comun. */
  subidaA?: number;
  /** Idem desde B. */
  subidaB?: number;
  /** Los ascendientes comunes mas cercanos (handles). */
  ancestros?: string[];
  /** Si el vinculo pasa por media sangre en algun punto. */
  mediaSangre?: boolean;
  /** Grado de primazgo, cuando aplica: 1 = primos hermanos. */
  gradoPrimo?: number;
  /** Veces que el primazgo esta "removido" (generaciones de desfase). */
  remocion?: number;
  /**
   * Vinculos ADICIONALES al que da el termino.
   *
   * Hace falta porque en un arbol con matrimonios entre ramas la misma
   * persona puede estar unida a uno por dos caminos a la vez: ser prima
   * segunda Y estar casada con un tio. El termino se queda con el de sangre,
   * que es el fuerte, y el otro camino se cuenta aqui en vez de perderse.
   */
  notas?: string[];
  /**
   * Cuando el parentesco es por afinidad y no tiene nombre propio, aqui va el
   * parentesco de sangre sobre el que se construye. De ahi sale el "politico".
   */
  baseAfin?: ParentescoBase;
}

/** Lo mismo sin el termino breve, que se calcula al final en un solo sitio. */
export type ParentescoBase = Omit<Parentesco, "breve">;

// ---------------------------------------------------------------------------
// Concordancia de genero
// ---------------------------------------------------------------------------

/**
 * Elige la forma segun el sexo. Para sexo desconocido se da la forma doble
 * ("hermano/a"), que es menos elegante pero no inventa un dato que no hay.
 */
function seg(sexo: Sexo, masc: string, fem: string): string {
  if (sexo === "M") return masc;
  if (sexo === "F") return fem;
  // Se construye "hermano/a" a partir de las dos formas.
  return formaDoble(masc, fem);
}

function formaDoble(masc: string, fem: string): string {
  const pm = masc.split(" ");
  const pf = fem.split(" ");
  if (pm.length !== pf.length) return `${masc}/${fem}`;
  return pm
    .map((w, i) => {
      const f = pf[i] as string;
      if (w === f) return w;
      // sufijo distinto: "hermano"/"hermana" -> "hermano/a"
      let i0 = 0;
      while (i0 < w.length && i0 < f.length && w[i0] === f[i0]) i0++;
      return `${w}/${f.slice(i0)}`;
    })
    .join(" ");
}

const ORDINALES_M = [
  "",
  "primero",
  "segundo",
  "tercero",
  "cuarto",
  "quinto",
  "sexto",
  "séptimo",
  "octavo",
  "noveno",
  "décimo",
];
const ORDINALES_F = [
  "",
  "primera",
  "segunda",
  "tercera",
  "cuarta",
  "quinta",
  "sexta",
  "séptima",
  "octava",
  "novena",
  "décima",
];

function ordinal(n: number, sexo: Sexo): string {
  const m = ORDINALES_M[n] ?? `${n}.º`;
  const f = ORDINALES_F[n] ?? `${n}.ª`;
  return seg(sexo, m, f);
}

/**
 * Ascendiente directo a `n` generaciones: padre, abuelo, bisabuelo,
 * tatarabuelo. Mas arriba el espanol no tiene terminos de uso comun y las
 * convenciones se contradicen entre si, asi que se dice en claro en vez de
 * elegir una al azar.
 */
function terminoAscendiente(n: number, sexo: Sexo): string {
  switch (n) {
    case 1:
      return seg(sexo, "padre", "madre");
    case 2:
      return seg(sexo, "abuelo", "abuela");
    case 3:
      return seg(sexo, "bisabuelo", "bisabuela");
    case 4:
      return seg(sexo, "tatarabuelo", "tatarabuela");
    default:
      return `${seg(sexo, "ascendiente", "ascendiente")} directo a ${n} generaciones`;
  }
}

function terminoDescendiente(n: number, sexo: Sexo): string {
  switch (n) {
    case 1:
      return seg(sexo, "hijo", "hija");
    case 2:
      return seg(sexo, "nieto", "nieta");
    case 3:
      return seg(sexo, "bisnieto", "bisnieta");
    case 4:
      return seg(sexo, "tataranieto", "tataranieta");
    default:
      return `${seg(sexo, "descendiente", "descendiente")} directo a ${n} generaciones`;
  }
}

/** "medio" o "media", concordando con la persona de la que se habla. */
function medioPrefijo(sexo: Sexo): string {
  return seg(sexo, "medio", "media");
}

/** tio, tio abuelo, tio bisabuelo... segun a que altura esta el hermano. */
function terminoTio(altura: number, sexo: Sexo, medio: boolean): string {
  const base =
    altura === 2
      ? seg(sexo, "tío", "tía")
      : altura === 3
        ? seg(sexo, "tío abuelo", "tía abuela")
        : altura === 4
          ? seg(sexo, "tío bisabuelo", "tía bisabuela")
          : altura === 5
            ? seg(sexo, "tío tatarabuelo", "tía tatarabuela")
            : `${seg(sexo, "tío", "tía")} a ${altura - 1} generaciones`;
  return medio ? `${medioPrefijo(sexo)} ${base}` : base;
}

function terminoSobrino(altura: number, sexo: Sexo, medio: boolean): string {
  const base =
    altura === 2
      ? seg(sexo, "sobrino", "sobrina")
      : altura === 3
        ? seg(sexo, "sobrino nieto", "sobrina nieta")
        : altura === 4
          ? seg(sexo, "sobrino bisnieto", "sobrina bisnieta")
          : `${seg(sexo, "sobrino", "sobrina")} a ${altura - 1} generaciones`;
  return medio ? `${medioPrefijo(sexo)} ${base}` : base;
}

/** "hermano", "media hermana"... entre dos personas concretas. */
function palabraHermano(ix: Indice, a: string, b: string): string {
  const s = sexoDe(ix, b);
  const c = ix.claseHermandad(a, b);
  if (c === "medio-paterno" || c === "medio-materno") {
    return `${medioPrefijo(s)} ${seg(s, "hermano", "hermana")}`;
  }
  return seg(s, "hermano", "hermana");
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

function sexoDe(ix: Indice, h: string): Sexo {
  return ix.persona(h)?.sexo ?? "U";
}

/**
 * El termino breve que corresponde a una posicion de parentesco, para el
 * sexo que se pida.
 *
 * Se construye desde la ESTRUCTURA (cuantas generaciones sube cada rama) y no
 * reaprovechando el texto ya calculado, porque hace falta poder decir lo
 * mismo con otro genero: la esposa de mi primo es "prima politica", y el
 * termino de el ("primo hermano") no sirve tal cual.
 */
function breveDeEstructura(r: ParentescoBase, sexo: Sexo): string {
  const a = r.subidaA ?? 0;
  const b = r.subidaB ?? 0;
  const medio = !!r.mediaSangre;

  if (a > 0 && b === 0) return terminoAscendiente(a, sexo);
  if (a === 0 && b > 0) return terminoDescendiente(b, sexo);
  if (a === 1 && b === 1) {
    return medio
      ? `${medioPrefijo(sexo)} ${seg(sexo, "hermano", "hermana")}`
      : seg(sexo, "hermano", "hermana");
  }

  const menor = Math.min(a, b);
  if (menor === 1) {
    return b === 1 ? terminoTio(a, sexo, medio) : terminoSobrino(b, sexo, medio);
  }

  const grado = menor - 1;
  const base =
    grado === 1
      ? seg(sexo, "primo hermano", "prima hermana")
      : `${seg(sexo, "primo", "prima")} ${ordinal(grado, sexo)}`;
  const remocion = Math.abs(a - b);
  // El desfase de generacion se marca en corto; el detalle esta en la ficha.
  return remocion ? `${base} (${remocion} gen.)` : base;
}

/** "prima politica", "tio politico": el afin que corresponde a un parentesco. */
function politico(base: string, sexo: Sexo): string {
  return `${base} ${seg(sexo, "político", "política")}`;
}

/** Los ascendientes de alguien, incluyendose a si misma a distancia 0. */
function ascendientesConUno(ix: Indice, h: string): Map<string, Ascendiente> {
  const m = new Map<string, Ascendiente>(ix.ascendientes(h));
  m.set(h, { dist: 0, viaPadre: false, viaMadre: false });
  return m;
}

function viaDe(a: Ascendiente | undefined): Via | undefined {
  if (!a) return undefined;
  if (a.viaPadre && a.viaMadre) return "ambas";
  if (a.viaPadre) return "paterna";
  if (a.viaMadre) return "materna";
  return undefined;
}

function textoVia(v: Via | undefined): string {
  if (v === "paterna") return " por vía paterna";
  if (v === "materna") return " por vía materna";
  if (v === "ambas") return " por las dos ramas";
  return "";
}

// ---------------------------------------------------------------------------
// Sangre
// ---------------------------------------------------------------------------

interface Cruce {
  subidaA: number;
  subidaB: number;
  ancestros: string[];
  via?: Via;
}

/**
 * El ascendiente comun mas cercano. "Mas cercano" = primero minimiza la
 * subida mayor de las dos, y a igualdad la suma. Es la definicion habitual y
 * es la que da el resultado que uno espera cuando las dos ramas no estan a la
 * misma altura.
 */
function cruceMasCercano(
  ascA: Map<string, Ascendiente>,
  ascB: Map<string, Ascendiente>,
): Cruce | undefined {
  let mejor: { mayor: number; suma: number } | undefined;
  let subidaA = 0;
  let subidaB = 0;
  let ancestros: string[] = [];

  for (const [h, ia] of ascA) {
    const ib = ascB.get(h);
    if (!ib) continue;
    const mayor = Math.max(ia.dist, ib.dist);
    const suma = ia.dist + ib.dist;
    if (!mejor || mayor < mejor.mayor || (mayor === mejor.mayor && suma < mejor.suma)) {
      mejor = { mayor, suma };
      subidaA = ia.dist;
      subidaB = ib.dist;
      ancestros = [h];
    } else if (mayor === mejor.mayor && suma === mejor.suma) {
      // Otro ascendiente comun igual de cerca: normalmente la pareja.
      if (ia.dist === subidaA && ib.dist === subidaB) ancestros.push(h);
    }
  }

  if (!mejor) return undefined;

  // La rama se toma del ascendiente comun elegido, acumulando los empatados.
  let viaPadre = false;
  let viaMadre = false;
  for (const h of ancestros) {
    const ia = ascA.get(h);
    if (ia?.viaPadre) viaPadre = true;
    if (ia?.viaMadre) viaMadre = true;
  }
  const via: Via | undefined =
    viaPadre && viaMadre ? "ambas" : viaPadre ? "paterna" : viaMadre ? "materna" : undefined;

  return { subidaA, subidaB, ancestros, via };
}

/**
 * El eslabon intermedio de una de las dos ramas: el ascendiente de `quien` a
 * distancia `dist` que ademas es hijo de alguno de los ascendientes comunes.
 *
 * Sirve para que las frases nombren a la persona real ("hermano de tu padre
 * Celestino") en vez de soltar una forma doble ("hermano de tu padre/madre"),
 * que es como quedaba antes y se leia fatal.
 */
function eslabon(
  ix: Indice,
  quien: string,
  asc: Map<string, Ascendiente>,
  dist: number,
  comunes: string[],
): string | undefined {
  if (dist === 0) return quien;
  for (const [h, info] of asc) {
    if (info.dist !== dist) continue;
    const padres = ix.progenitoresPlanos(h);
    if (comunes.some((c) => padres.includes(c))) return h;
  }
  return undefined;
}

/**
 * El ascendiente de `quien` que esta a `dist` generaciones de el y a
 * `distDesdeEslabon` generaciones del ascendiente comun.
 *
 * Sirve para los primos con desfase de generacion: permite decir "prima
 * hermana de tu padre Celestino" en vez de "prima hermana de tu padre/madre".
 * Solo se usa al responder una consulta concreta, no al dibujar, asi que el
 * coste de recorrer ascendientes aqui no importa.
 */
function eslabonADistancia(
  ix: Indice,
  asc: Map<string, Ascendiente>,
  dist: number,
  comunes: string[],
  distDesdeEslabon: number,
): string | undefined {
  for (const [h, info] of asc) {
    if (info.dist !== dist) continue;
    const arriba = ix.ascendientes(h);
    if (comunes.some((c) => arriba.get(c)?.dist === distDesdeEslabon)) return h;
  }
  return undefined;
}

function nombreCorto(ix: Indice, h: string): string {
  const p = ix.persona(h);
  if (!p) return h;
  const n = p.nombres[0];
  const pila = n?.first?.trim();
  const ap = n?.apellidos
    .map((x) => x.texto.trim())
    .filter(Boolean)
    .join(" ");
  return [pila, ap].filter(Boolean).join(" ") || (p.id ?? h);
}

/** Parentesco de sangre, o undefined si no hay ascendiente comun. */
function porSangre(ix: Indice, a: string, b: string): ParentescoBase | undefined {
  const ascA = ascendientesConUno(ix, a);
  const ascB = ascendientesConUno(ix, b);
  const cruce = cruceMasCercano(ascA, ascB);
  if (!cruce) return undefined;

  const { subidaA, subidaB, ancestros, via } = cruce;
  const sb = sexoDe(ix, b);
  const comun = ancestros.map((h) => nombreCorto(ix, h)).join(" y ");

  /**
   * Media sangre.
   *
   * Se decide por el VINCULO DE HERMANDAD del eslabon intermedio, no contando
   * cuantos ascendientes comunes hay. Contar ascendientes parecia equivalente
   * y no lo es: cuando una familia tiene madre registrada y padre desconocido
   * -- en el arbol real hay siete -- dos primos carnales comparten un solo
   * abuelo CONOCIDO, y contando salian marcados como media sangre. Eso no es
   * media sangre, es un dato que falta.
   */
  const eslA = subidaA >= 1 ? eslabon(ix, a, ascA, subidaA - 1, ancestros) : undefined;
  const eslB = subidaB >= 1 ? eslabon(ix, b, ascB, subidaB - 1, ancestros) : undefined;
  const claseEslabon =
    eslA && eslB && eslA !== eslB ? ix.claseHermandad(eslA, eslB) : undefined;
  const medio =
    claseEslabon === "medio-paterno" || claseEslabon === "medio-materno";

  const base = {
    clase: "sanguineo" as const,
    subidaA,
    subidaB,
    ancestros,
    via,
    mediaSangre: medio,
  };

  // --- linea directa -----------------------------------------------------
  if (subidaA === 0 && subidaB === 0) {
    return { ...base, termino: "la misma persona", explicacion: "es la misma persona", clase: "misma" };
  }
  if (subidaB === 0) {
    // B es ascendiente de A.
    const t = terminoAscendiente(subidaA, sb);
    return {
      ...base,
      termino: t,
      explicacion: `es tu ${t}${textoVia(via)}`,
    };
  }
  if (subidaA === 0) {
    // B desciende de A.
    const t = terminoDescendiente(subidaB, sb);
    return { ...base, termino: t, explicacion: `es tu ${t}` };
  }

  // --- hermanos ----------------------------------------------------------
  if (subidaA === 1 && subidaB === 1) {
    const clase = ix.claseHermandad(a, b);
    const esMedio = clase === "medio-paterno" || clase === "medio-materno";
    const t = esMedio
      ? seg(sb, "medio hermano", "media hermana")
      : seg(sb, "hermano", "hermana");
    const detalle =
      clase === "medio-paterno"
        ? ": comparten padre"
        : clase === "medio-materno"
          ? ": comparten madre"
          : "";
    return {
      ...base,
      mediaSangre: esMedio,
      termino: t,
      explicacion: `es tu ${t}${detalle} (${comun})`,
    };
  }

  // --- colaterales -------------------------------------------------------
  const menor = Math.min(subidaA, subidaB);
  const remocion = Math.abs(subidaA - subidaB);

  if (menor === 1) {
    // Uno de los dos es hijo directo del ascendiente comun: tios y sobrinos.
    if (subidaB === 1) {
      // B es hermano de un ascendiente de A.
      const t = terminoTio(subidaA, sb, medio);
      const esl = eslabon(ix, a, ascA, subidaA - 1, ancestros);
      const deQuien = esl
        ? `tu ${terminoAscendiente(subidaA - 1, sexoDe(ix, esl))} ${nombreCorto(ix, esl)}`
        : `tu ${terminoAscendiente(subidaA - 1, "U")}`;
      const comoHermano = esl ? palabraHermano(ix, esl, b) : seg(sb, "hermano", "hermana");
      return {
        ...base,
        termino: t,
        explicacion: `es tu ${t}${textoVia(via)}: ${comoHermano} de ${deQuien}`,
      };
    }
    // B desciende de un hermano de A.
    const t = terminoSobrino(subidaB, sb, medio);
    const esl = eslabon(ix, b, ascB, subidaB - 1, ancestros);
    const comoHermano = esl ? palabraHermano(ix, a, esl) : seg("U", "hermano", "hermana");
    const deQuien = esl ? `tu ${comoHermano} ${nombreCorto(ix, esl)}` : `tu ${comoHermano}`;
    return {
      ...base,
      termino: t,
      explicacion: `es tu ${t}: ${terminoDescendiente(subidaB - 1, sb)} de ${deQuien}`,
    };
  }

  // Primos. grado 1 = primos hermanos.
  const grado = menor - 1;
  const t0 =
    grado === 1
      ? seg(sb, "primo hermano", "prima hermana")
      : `${seg(sb, "primo", "prima")} ${ordinal(grado, sb)}`;
  const t = medio ? `${t0} por una sola rama` : t0;

  if (remocion === 0) {
    return {
      ...base,
      gradoPrimo: grado,
      remocion,
      termino: t,
      explicacion: `es tu ${t}${textoVia(via)} (ascendiente común: ${comun})`,
    };
  }

  // Primos con desfase de generacion. Se da el termino canonico y ademas la
  // forma coloquial, que es la que de verdad se entiende.
  const veces = remocion === 1 ? "una vez" : `${remocion} veces`;
  const canonico = `${t} ${veces} ${seg(sb, "removido", "removida")}`;

  /** El primo de grado `grado`, concordando con quien sea. */
  const primoDe = (s: Sexo): string =>
    grado === 1
      ? seg(s, "primo hermano", "prima hermana")
      : `${seg(s, "primo", "prima")} ${ordinal(grado, s)}`;

  let coloquial: string;
  if (subidaB < subidaA) {
    // B es primo de un ascendiente de A.
    const esl = eslabonADistancia(ix, ascA, remocion, ancestros, subidaB);
    coloquial = esl
      ? `${primoDe(sb)} de tu ${terminoAscendiente(remocion, sexoDe(ix, esl))} ${nombreCorto(ix, esl)}`
      : `${primoDe(sb)} de un ${terminoAscendiente(remocion, "M")} tuyo`;
  } else {
    // B desciende de un primo de A.
    const esl = eslabonADistancia(ix, ascB, remocion, ancestros, subidaA);
    coloquial = esl
      ? `${terminoDescendiente(remocion, sb)} de tu ${primoDe(sexoDe(ix, esl))} ${nombreCorto(ix, esl)}`
      : `${terminoDescendiente(remocion, sb)} de un ${primoDe("M")} tuyo`;
  }

  return {
    ...base,
    gradoPrimo: grado,
    remocion,
    termino: canonico,
    explicacion: `es tu ${canonico}${textoVia(via)}; dicho en claro: ${coloquial}`,
  };
}

// ---------------------------------------------------------------------------
// Afinidad
// ---------------------------------------------------------------------------

function palabraPareja(rel: string, sexo: Sexo): string {
  if (rel === "Married") return seg(sexo, "esposo", "esposa");
  if (rel === "Unmarried") return seg(sexo, "compañero", "compañera");
  return seg(sexo, "pareja", "pareja");
}

/** Parentesco por afinidad: un solo salto de pareja, en los dos sentidos. */
function porAfinidad(ix: Indice, a: string, b: string): ParentescoBase | undefined {
  const sb = sexoDe(ix, b);

  // --- B es pareja de A --------------------------------------------------
  for (const v of ix.parejas(a)) {
    if (v.otra === b) {
      const t = palabraPareja(v.rel, sb);
      const nota =
        v.rel === "Unknown" ? " (el tipo de unión no está registrado)" : "";
      return { clase: "afin", termino: t, explicacion: `es tu ${t}${nota}` };
    }
  }

  // --- B es familiar de sangre de una pareja de A -------------------------
  for (const v of ix.parejas(a)) {
    const r = porSangre(ix, v.otra, b);
    if (!r || r.clase === "misma") continue;
    const miPareja = palabraPareja(v.rel, sexoDe(ix, v.otra));

    // padres de mi pareja -> suegros
    if (r.subidaA === 1 && r.subidaB === 0) {
      const t = seg(sb, "suegro", "suegra");
      return { clase: "afin", termino: t, explicacion: `es tu ${t}: ${r.termino} de tu ${miPareja}` };
    }
    // hermanos de mi pareja -> cunados
    if (r.subidaA === 1 && r.subidaB === 1) {
      const t = seg(sb, "cuñado", "cuñada");
      const medio = r.mediaSangre ? " (medio hermano de tu pareja)" : "";
      return { clase: "afin", termino: t, explicacion: `es tu ${t}: ${r.termino} de tu ${miPareja}${medio}` };
    }
    // hijos de mi pareja que no son mios -> hijastros
    if (r.subidaA === 0 && r.subidaB === 1) {
      const mios = ix.hijosDe(a);
      if (!mios.includes(b)) {
        const t = seg(sb, "hijastro", "hijastra");
        return { clase: "afin", termino: t, explicacion: `es tu ${t}: ${r.termino} de tu ${miPareja}` };
      }
    }
    return {
      clase: "afin",
      termino: `${r.termino} de tu ${miPareja}`,
      explicacion: `no hay parentesco de sangre: es ${r.termino} de tu ${miPareja}`,
      baseAfin: r,
    };
  }

  // --- B es pareja de un familiar de sangre de A -------------------------
  for (const v of ix.parejas(b)) {
    const r = porSangre(ix, a, v.otra);
    if (!r || r.clase === "misma") continue;
    const suPareja = palabraPareja(v.rel, sb);
    const deQuien = nombreCorto(ix, v.otra);

    // pareja de mi hijo -> yerno / nuera
    if (r.subidaA === 0 && r.subidaB === 1) {
      const t = seg(sb, "yerno", "nuera");
      return { clase: "afin", termino: t, explicacion: `es tu ${t}: ${suPareja} de tu ${r.termino} ${deQuien}` };
    }
    // pareja de mi hermano -> cunado
    if (r.subidaA === 1 && r.subidaB === 1) {
      const t = seg(sb, "cuñado", "cuñada");
      return { clase: "afin", termino: t, explicacion: `es tu ${t}: ${suPareja} de tu ${r.termino} ${deQuien}` };
    }
    // pareja de mi padre o madre que no es mi progenitor -> padrastro
    if (r.subidaA === 1 && r.subidaB === 0) {
      const mios = ix.progenitoresPlanos(a);
      if (!mios.includes(b)) {
        const t = seg(sb, "padrastro", "madrastra");
        return { clase: "afin", termino: t, explicacion: `es tu ${t}: ${suPareja} de tu ${r.termino} ${deQuien}` };
      }
    }
    return {
      clase: "afin",
      termino: `${suPareja} de tu ${r.termino}`,
      explicacion: `no hay parentesco de sangre: es ${suPareja} de tu ${r.termino} ${deQuien}`,
      baseAfin: r,
    };
  }

  // --- hermanastros: hijo de la pareja de mi progenitor -------------------
  for (const g of ix.progenitoresPlanos(a)) {
    for (const v of ix.parejas(g)) {
      if (ix.progenitoresPlanos(a).includes(v.otra)) continue; // es mi otro progenitor
      if (ix.hijosDe(v.otra).includes(b) && !ix.hermanos(a).some((x) => x.otra === b)) {
        const t = seg(sb, "hermanastro", "hermanastra");
        return {
          clase: "afin",
          termino: t,
          explicacion: `es tu ${t}: ${seg(sb, "hijo", "hija")} de ${nombreCorto(ix, v.otra)}, que es pareja de tu ${terminoAscendiente(1, sexoDe(ix, g))}, sin sangre en común contigo`,
        };
      }
    }
  }

  // --- concunos: pareja de un hermano de mi pareja -----------------------
  for (const v of ix.parejas(a)) {
    for (const h of ix.hermanos(v.otra)) {
      for (const w of ix.parejas(h.otra)) {
        if (w.otra === b) {
          const t = seg(sb, "concuño", "concuña");
          return {
            clase: "afin",
            termino: t,
            explicacion: `es tu ${t}: pareja de ${seg(sexoDe(ix, h.otra), "un hermano", "una hermana")} de tu ${palabraPareja(v.rel, sexoDe(ix, v.otra))}`,
          };
        }
      }
    }
  }

  return undefined;
}

/**
 * Vinculos de pareja que existen ADEMAS del parentesco de sangre encontrado.
 *
 * En arboles con matrimonios entre ramas esto pasa de verdad: alguien es
 * prima hermana del padre y al mismo tiempo esposa del tio. Sin esto, el
 * segundo camino no se ve en ninguna parte.
 */
function notasAfines(ix: Indice, a: string, b: string): string[] {
  const notas: string[] = [];
  const sb = sexoDe(ix, b);

  for (const v of ix.parejas(b)) {
    if (v.otra === a) {
      notas.push(`además es tu ${palabraPareja(v.rel, sb)}`);
      continue;
    }
    const r = porSangre(ix, a, v.otra);
    if (!r || r.clase === "misma" || r.clase === "ninguno") continue;
    notas.push(
      `además es ${palabraPareja(v.rel, sb)} de tu ${r.termino} ` +
        nombreCorto(ix, v.otra),
    );
  }
  return notas;
}

// ---------------------------------------------------------------------------
// Entrada principal
// ---------------------------------------------------------------------------

/**
 * Que es B de A.
 *
 * Primero sangre, luego afinidad. Si no hay nada, se dice que no hay
 * parentesco registrado, que no es lo mismo que decir que no existe.
 */
export function parentesco(ix: Indice, a: string, b: string): Parentesco {
  const sb = sexoDe(ix, b);

  /** Anade el termino breve, que es lo que se pinta bajo cada nombre. */
  const completar = (r: ParentescoBase): Parentesco => {
    let breve: string;
    if (r.clase === "misma") breve = "la misma persona";
    else if (r.clase === "ninguno") breve = "";
    else if (r.clase === "sanguineo") breve = breveDeEstructura(r, sb);
    else if (r.baseAfin) breve = politico(breveDeEstructura(r.baseAfin, sb), sb);
    // Los afines con nombre propio (esposa, cunado, suegro, yerno, nuera,
    // concuno, madrastra, hijastro) ya son cortos tal cual.
    else breve = r.termino;
    return { ...r, breve };
  };

  if (a === b) {
    return completar({
      clase: "misma",
      termino: "la misma persona",
      explicacion: "es la misma persona",
    });
  }

  // Hermandad declarada por una familia sin progenitores: no hay ascendiente
  // comun que encontrar, hay que preguntarlo.
  const clase = ix.claseHermandad(a, b);
  if (clase === "declarados") {
    const t = seg(sb, "hermano", "hermana");
    return completar({
      clase: "sanguineo",
      termino: t,
      subidaA: 1,
      subidaB: 1,
      explicacion:
        `es tu ${t}: estan declarados como hermanos en una familia sin ` +
        "padre ni madre registrados, así que no se sabe por quién",
    });
  }

  const sangre = porSangre(ix, a, b);
  if (sangre) {
    const notas = notasAfines(ix, a, b);
    return completar(notas.length ? { ...sangre, notas } : sangre);
  }

  const afin = porAfinidad(ix, a, b);
  if (afin) return completar(afin);

  return completar({
    clase: "ninguno",
    termino: "sin parentesco registrado",
    explicacion:
      "no hay ningún camino de parentesco en los datos. Puede que exista y " +
      "falte registrarlo.",
  });
}
